import React, { useState, useEffect } from 'react';
import { Bell, X, Check } from 'lucide-react';
import api from '../services/api';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';

interface Notification {
  id: number;
  notification_type: string;
  title: string;
  message: string;
  appointment?: number;
  is_read: boolean;
  created_at: string;
}

const NotificationBell: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showPanel, setShowPanel] = useState(false);
  const [loading, setLoading] = useState(false);
  const { handleAuthError } = useAuthErrorHandler();

  useEffect(() => {
    loadNotifications();
    // Polling cada 30 segundos para nuevas notificaciones
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return; // No hay usuario autenticado

      const [notifRes, countRes] = await Promise.all([
        api.get('appointments/notifications/'),
        api.get('appointments/notifications/unread_count/'),
      ]);

      const notifData = notifRes.data;
      const notifications = Array.isArray(notifData) ? notifData : notifData.results || [];
      setNotifications(notifications);
      setUnreadCount(countRes.data.unread_count || 0);
    } catch (err) {
      console.error('Error cargando notificaciones', err);
      handleAuthError(err, 'carga de notificaciones');
    }
  };

  const markAsRead = async (id: number) => {
    try {
      await api.post(`appointments/notifications/${id}/mark_read/`);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marcando notificación como leída', err);
      handleAuthError(err, 'marcar notificación como leída');
    }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await api.post('appointments/notifications/mark_all_read/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marcando todas como leídas', err);
      handleAuthError(err, 'marcar todas como leídas');
    } finally {
      setLoading(false);
    }
  };

  const deleteNotification = async (id: number) => {
    try {
      await api.delete(`appointments/notifications/${id}/`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setUnreadCount(prev => {
        const notification = notifications.find(n => n.id === id);
        return notification && !notification.is_read ? Math.max(0, prev - 1) : prev;
      });
    } catch (err) {
      console.error('Error eliminando notificación', err);
      handleAuthError(err, 'eliminar notificación');
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins} min`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    return date.toLocaleDateString('es-ES');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_booking':
        return '📅';
      case 'booking_updated':
        return '✏️';
      case 'booking_cancelled':
        return '❌';
      case 'professional_assigned':
        return '👤';
      case 'reminder':
        return '⏰';
      default:
        return '📢';
    }
  };

  // No mostrar nada si no hay token (usuario no autenticado)
  const token = localStorage.getItem('access_token');
  if (!token) return null;

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 transform translate-x-1/4 -translate-y-1/4 w-4 h-4 bg-primary-accent text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel */}
      {showPanel && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowPanel(false)}
          ></div>

          {/* Panel anclado al ícono */}
          <div className="absolute top-full right-0 mt-2 w-80 md:w-96 max-w-[92vw] bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Notificaciones</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    disabled={loading}
                    className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                  >
                    Marcar todas
                  </button>
                )}
                <button
                  onClick={() => setShowPanel(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="overflow-y-auto flex-1">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <Bell className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No tienes notificaciones</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {notifications.map(notif => (
                    <li
                      key={notif.id}
                      className={`p-4 hover:bg-gray-50 transition-colors ${
                        !notif.is_read ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">{getNotificationIcon(notif.notification_type)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="font-medium text-sm text-gray-900">{notif.title}</p>
                            {!notif.is_read && (
                              <button
                                onClick={() => markAsRead(notif.id)}
                                className="text-blue-600 hover:text-blue-700"
                                title="Marcar como leída"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                          <div className="flex items-center justify-between mt-2">
                            <p className="text-xs text-gray-500">{formatTimeAgo(notif.created_at)}</p>
                            <button
                              onClick={() => deleteNotification(notif.id)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Eliminar
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
